import { useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Check from '@mui/icons-material/Check';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';

import flagArabic from '@/assets/images/arabic.png';
import flagEnglish from '@/assets/images/england.png';
import flagFrench from '@/assets/images/france.png';

type LangOption = {
  code: string;
  label: string;
  flag: string;
};

const LANGUAGES: LangOption[] = [
  { code: 'en', label: 'English', flag: flagEnglish },
  { code: 'fr', label: 'Français', flag: flagFrench },
  { code: 'ar', label: 'عربي', flag: flagArabic },
];

function getCurrentLangOption(lang: string): LangOption {
  return (
    LANGUAGES.find((l) => lang === l.code || lang.startsWith(l.code)) ??
    (LANGUAGES[0] as LangOption)
  );
}

export function LanguageSwitcher() {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const currentOption = getCurrentLangOption(currentLang);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  // Direction + document attributes are handled by useDirectionStore's
  // i18n.on('languageChanged') listener — no manual sync needed here.
  const handleChange = (lang: LangOption) => {
    handleClose();
    void i18n.changeLanguage(lang.code);
  };

  // Small devices: compact button + dropdown (flag + chevron, white panel, rows with checkmark)
  if (isSmall) {
    return (
      <>
        <ButtonBase
          id="language-switcher-button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={currentOption.label}
          onClick={handleOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            height: 43,
            px: 1.25,
            borderRadius: '12px',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: '0px 1px 2px rgba(10,13,20,0.06)',
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src={currentOption.flag}
            alt=""
            aria-hidden="true"
            sx={{
              width: 20,
              height: 20,
              objectFit: 'cover',
              borderRadius: '3px',
            }}
          />
          {open ? (
            <ExpandLess sx={{ fontSize: 20, color: 'text.secondary' }} />
          ) : (
            <ExpandMore sx={{ fontSize: 20, color: 'text.secondary' }} />
          )}
        </ButtonBase>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: {
                mt: 1.5,
                minWidth: 180,
                borderRadius: '12px',
                boxShadow: '0px 4px 12px rgba(10,13,20,0.12)',
                py: 0.5,
              },
            },
          }}
          MenuListProps={{
            'aria-labelledby': 'language-switcher-button',
          }}
        >
          {LANGUAGES.map((lang) => {
            const isActive = currentLang === lang.code || currentLang.startsWith(lang.code);
            return (
              <MenuItem
                key={lang.code}
                selected={isActive}
                onClick={() => handleChange(lang)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  py: 1.25,
                  px: 2,
                  bgcolor: isActive ? 'primary.light' : 'transparent',
                  '&:hover': {
                    bgcolor: isActive ? 'primary.light' : 'action.hover',
                  },
                }}
              >
                <Box
                  component="img"
                  src={lang.flag}
                  alt=""
                  aria-hidden="true"
                  sx={{
                    width: 24,
                    height: 24,
                    objectFit: 'cover',
                    borderRadius: '4px',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: 14,
                    fontWeight: isActive ? 600 : 400,
                    color: 'text.primary',
                  }}
                >
                  {lang.label}
                </Typography>
                {isActive ? <Check sx={{ fontSize: 20, color: 'surface.iconColor' }} /> : null}
              </MenuItem>
            );
          })}
        </Menu>
      </>
    );
  }

  // Desktop: existing pill with all three options inline
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        bgcolor: 'background.default',
        borderRadius: '12px',
        p: '4px',
        height: 43,
        flexShrink: 0,
      }}
    >
      {LANGUAGES.map((lang) => {
        const isActive = currentLang === lang.code || currentLang.startsWith(lang.code);
        return (
          <ButtonBase
            key={lang.code}
            onClick={() => handleChange(lang)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              px: '4px',
              height: '100%',
              width: 100,
              borderRadius: '10px',
              bgcolor: isActive ? 'background.paper' : 'transparent',
              overflow: 'hidden',
              transition: 'background-color 0.15s ease',
              '&:hover': {
                bgcolor: isActive ? 'background.paper' : 'rgba(0,0,0,0.04)',
              },
            }}
          >
            <Box
              component="img"
              src={lang.flag}
              alt=""
              aria-hidden="true"
              sx={{
                width: 18,
                height: 18,
                objectFit: 'cover',
                borderRadius: '3px',
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontWeight: isActive ? 500 : 400,
                color: isActive ? 'text.strong' : 'text.icon',
                fontSize: 14,
                lineHeight: '20px',
                letterSpacing: '-0.084px',
                whiteSpace: 'nowrap',
              }}
            >
              {lang.label}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
}
